'use client';

import { useEffect, useRef, useState } from 'react';

export type TrustpilotWidgetProps = {
  locale?: string;
  templateId?: string;
  businessUnitId?: string;
  styleHeight?: string;
  styleWidth?: string;
  theme?: 'light' | 'dark';
  textAlign?: 'left' | 'center' | 'right';
  token?: string;
  reviewUrl?: string;
  className?: string;
};

export const TrustpilotWidget = ({
  locale = 'en-GB',
  templateId = '5419b732fbfb950b10de65e5',
  businessUnitId = '601aaf6db98c5900019563b2',
  styleHeight = '24px',
  styleWidth = '100%',
  theme = 'dark',
  textAlign,
  token = 'edc8f22e-9404-45c2-8df7-eef07e9542cf',
  reviewUrl = 'https://uk.trustpilot.com/review/www.constructionline.co.uk',
  className,
}: TrustpilotWidgetProps) => {
  const [isClient, setIsClient] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || !widgetRef.current) return;

    // Clear any existing content
    widgetRef.current.innerHTML = '';

    // Create the widget element with data attributes
    const widgetElement = document.createElement('div');
    widgetElement.className = 'trustpilot-widget';
    widgetElement.setAttribute('data-locale', locale);
    widgetElement.setAttribute('data-template-id', templateId);
    widgetElement.setAttribute('data-businessunit-id', businessUnitId);
    widgetElement.setAttribute('data-style-height', styleHeight);
    widgetElement.setAttribute('data-style-width', styleWidth);
    widgetElement.setAttribute('data-theme', theme);
    if (textAlign) widgetElement.setAttribute('data-text-align', textAlign);
    widgetElement.setAttribute('data-token', token);

    // Add fallback content
    const fallbackLink = document.createElement('a');
    fallbackLink.href = reviewUrl;
    fallbackLink.target = '_blank';
    fallbackLink.rel = 'noopener';
    fallbackLink.textContent = 'Trustpilot';
    widgetElement.appendChild(fallbackLink);

    widgetRef.current.appendChild(widgetElement);

    // Initialize Trustpilot widget if the script is available
    if (typeof window !== 'undefined' && window.Trustpilot) {
      try {
        window.Trustpilot.loadFromElement(widgetElement);
      } catch (error) {
        console.warn('Failed to load Trustpilot widget:', error);
      }
    }
  }, [
    isClient,
    locale,
    templateId,
    businessUnitId,
    styleHeight,
    styleWidth,
    theme,
    textAlign,
    token,
    reviewUrl,
  ]);

  // Render a placeholder on the server to prevent hydration mismatch
  if (!isClient) {
    return (
      <>
        <script
          type="text/javascript"
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          async
        ></script>
        <div className={className}>
          <a href={reviewUrl} target="_blank" rel="noopener">
            Trustpilot
          </a>
        </div>
      </>
    );
  }

  return <div ref={widgetRef} className={className} />;
};

TrustpilotWidget.displayName = 'TrustpilotWidget';
